import { useState } from "react";
import { EmptyState, Spinner, MDisplay, MEyebrow } from "@cntrl/ui";
import { RequireBand } from "../../app/RequireBand.js";
import { useMobileBack } from "../../app/mobileBack.js";
import { useVip } from "../../app/hooks/useShows.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import { ApiError } from "../../app/hooks/apiError.js";
import styles from "./roadCases.module.scss";

/**
 * `/shows/:id/vip` — the mobile VIP list + search. Reads the EXISTING VIP block
 * (`GET /shows/{id}/vip` → `{ entryTime, source, list }`; the `list` is a pasted text roster), so
 * adds NO backend here. Live via the `show:{id}` group (a VIP PUT emits `schedule-changed`). Money-free.
 * Search filters the roster lines client-side (the `list` is free text — one name per line). Designed empty state
 * for a show with no VIP set (born-blank).
 */
export function ShowVipScreen({ showId, onBack }: { showId: string; onBack?: () => void }) {
  return (
    <RequireBand>
      <VipInner showId={showId} onBack={onBack} />
    </RequireBand>
  );
}

function VipInner({ showId, onBack }: { showId: string; onBack?: () => void }) {
  useLiveGroup(`show:${showId}`);
  useMobileBack(onBack); // back rides the shell header's leading slot (logo-replacement), not the body
  const q = useVip(showId);
  const [term, setTerm] = useState("");

  if (q.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={22} label="Loading VIP list" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className={styles.screen}>
        <EmptyState
          title="Couldn't load VIP list"
          description={q.error instanceof ApiError ? q.error.message : "Check your connection and try again."}
        />
      </div>
    );
  }

  const vip = q.data;
  const lines = (vip?.list ?? "").split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  const hasAny = Boolean(vip?.entryTime) || Boolean(vip?.source) || lines.length > 0;
  const needle = term.trim().toLowerCase();
  const filtered = needle ? lines.filter((l) => l.toLowerCase().includes(needle)) : lines;

  return (
    <div className={styles.screen}>
      <header className={styles.headerRow}>
        <div className={styles.headerText}>
          <MEyebrow>VIP</MEyebrow>
          <MDisplay>Guest of honour</MDisplay>
        </div>
      </header>

      {!hasAny ? (
        <EmptyState
          title="No VIP set"
          description="Entry time, source, and the VIP list appear here once the show's VIP section is filled in."
        />
      ) : (
        <>
          {(vip?.entryTime || vip?.source) && (
            <div className={styles.pairPanel}>
              {vip?.entryTime && <p className={styles.hint}>Entry {vip.entryTime.slice(0, 5)}</p>}
              {vip?.source && <p className={styles.hint}>Source: {vip.source}</p>}
            </div>
          )}

          {lines.length > 0 && (
            <div className={styles.newRow}>
              <input
                className={styles.input}
                type="search"
                inputMode="search"
                placeholder="Search VIP list"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                aria-label="Search VIP list"
              />
            </div>
          )}

          {lines.length === 0 ? (
            <p className={styles.hint}>No names on the VIP list yet.</p>
          ) : filtered.length === 0 ? (
            <EmptyState title="No matches" description={`No VIP entry matches "${term.trim()}".`} />
          ) : (
            <ul className={styles.list}>
              {filtered.map((line, i) => (
                <li key={`${i}-${line}`} className={styles.caseRow}>
                  <span className={styles.caseName}>{line}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
